import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import './App.css';

// Components
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Switch } from './components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { ScrollArea } from './components/ui/scroll-area';
import { Separator } from './components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './components/ui/sheet';
import { Calendar } from './components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';

// Icons
import { 
  ShoppingCart, Menu, X, Plus, Minus, Trash2, User, LogOut, Settings, 
  ChefHat, Leaf, Flame, MessageCircle, Send, Calendar as CalendarIcon,
  Clock, Users, Phone, Mail, MapPin, ArrowRight, Star, Utensils,
  Home, BookOpen, ShoppingBag, Info
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============== CONTEXTS ==============

const AuthContext = createContext();
const CartContext = createContext();
const SettingsContext = createContext();

export const useAuth = () => useContext(AuthContext);
export const useCart = () => useContext(CartContext);
export const useSettings = () => useContext(SettingsContext);

// ============== AUTH PROVIDER ==============

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUser(response.data);
    } catch {
      setUser(null);
      localStorage.removeItem('token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    localStorage.setItem('token', response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, googleLogin, token, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============== CART PROVIDER ==============

const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  const addItem = (item) => {
    setItems(prev => {
      const existing = prev.find(i => i.item_id === item.item_id);
      if (existing) {
        return prev.map(i => i.item_id === item.item_id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...item, quantity: 1 }];
    });
    toast.success(`Added ${item.name} to cart`);
  };

  const removeItem = (itemId) => {
    setItems(prev => prev.filter(i => i.item_id !== itemId));
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }
    setItems(prev => prev.map(i => i.item_id === itemId ? { ...i, quantity } : i));
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem('cart');
  };

  const total = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, total, itemCount }}>
      {children}
    </CartContext.Provider>
  );
};

// ============== SETTINGS PROVIDER ==============

const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, setSettings, loading }}>
      {children}
    </SettingsContext.Provider>
  );
};

// ============== NAVBAR ==============

const Navbar = () => {
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { to: '/', label: 'Home', icon: Home },
    { to: '/menu', label: 'Menu', icon: BookOpen },
    { to: '/about', label: 'About', icon: Info },
  ];

  return (
    <nav className="sticky top-0 z-50 w-full backdrop-blur-md bg-background/80 border-b border-border/50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img 
              src="/assets/hs-logo.png" 
              alt="H&S Smoothies Logo" 
              className="h-12 w-auto"
            />
            <span className="font-serif text-xl md:text-2xl text-foreground">H&S Smoothies</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <Link 
                key={link.to} 
                to={link.to} 
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors tracking-wide"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4">
            <Link to="/cart" className="relative" data-testid="nav-cart">
              <Button variant="ghost" size="icon">
                <ShoppingCart className="w-5 h-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-secondary text-secondary-foreground text-xs font-bold rounded-full flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Button>
            </Link>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2" data-testid="user-menu-trigger">
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">{user.name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/account" data-testid="nav-account">My Orders</Link>
                  </DropdownMenuItem>
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" data-testid="nav-admin">Admin Dashboard</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={logout} data-testid="nav-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <Button variant="outline" data-testid="nav-login">Sign In</Button>
              </Link>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetHeader>
                  <SheetTitle className="font-serif">Menu</SheetTitle>
                </SheetHeader>
                <div className="mt-8 flex flex-col gap-4">
                  {navLinks.map(link => (
                    <Link 
                      key={link.to} 
                      to={link.to} 
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 text-lg font-medium text-foreground hover:text-primary transition-colors"
                    >
                      <link.icon className="w-5 h-5" />
                      {link.label}
                    </Link>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

// ============== FOOTER ==============

const Footer = () => {
  const { settings } = useSettings();

  return (
    <footer className="bg-foreground text-background py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="/assets/hs-logo.png" 
                alt="H&S Smoothies Logo" 
                className="h-12 w-auto"
              />
              <span className="font-serif text-xl">H&S Smoothies</span>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Fresh smoothies, delicious breakfast, and authentic Eritrean favorites in Scottdale, GA.
            </p>
          </div>
          <div>
            <h4 className="font-serif text-lg mb-4">Hours</h4>
            <div className="flex flex-col gap-1 text-sm text-muted-foreground">
              <div className="flex justify-between"><span>Mon-Sat</span><span>8AM-5PM</span></div>
              <div className="flex justify-between"><span>Sunday</span><span>8AM-4PM</span></div>
            </div>
          </div>
          <div>
            <h4 className="font-serif text-lg mb-4">Quick Links</h4>
            <div className="flex flex-col gap-2">
              <Link to="/menu" className="text-sm text-muted-foreground hover:text-secondary transition-colors">Our Menu</Link>
              <Link to="/about" className="text-sm text-muted-foreground hover:text-secondary transition-colors">About Us</Link>
              <Link to="/cart" className="text-sm text-muted-foreground hover:text-secondary transition-colors">Order Online</Link>
            </div>
          </div>
          <div>
            <h4 className="font-serif text-lg mb-4">Contact</h4>
            <div className="flex flex-col gap-2 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>3500 N Decatur Rd, Scottdale, GA 30079</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <span>(404) 500-4648</span>
              </div>
            </div>
          </div>
        </div>
        <Separator className="my-8 bg-muted-foreground/20" />
        <p className="text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} H&S Smoothies & Breakfast. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

// ============== HOME PAGE ==============

const HomePage = () => {
  const [featured, setFeatured] = useState([]);
  const { addItem } = useCart();
  const { settings } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API}/menu/featured`);
        setFeatured(response.data);
      } catch (err) {
        console.error('Failed to fetch featured items:', err);
      }
    };
    fetchFeatured();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_food-commerce-5/artifacts/g8ps34gw_image.png')` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="max-w-2xl">
            <span className="font-accent text-secondary text-2xl mb-4 block animate-fadeInUp">Welcome to</span>
            <h1 className="font-serif text-5xl md:text-7xl text-white tracking-tight leading-none mb-6 animate-fadeInUp stagger-1">
              H&S Smoothies & Breakfast
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-8 animate-fadeInUp stagger-2">
              Fresh smoothies, delicious breakfast, and authentic Eritrean favorites. Start your day right in Scottdale, GA.
            </p>
            <div className="flex flex-wrap gap-4 animate-fadeInUp stagger-3">
              <Button 
                size="lg" 
                className="bg-primary text-white hover:bg-primary/90 shadow-md transition-all hover:-translate-y-0.5 font-medium px-8"
                onClick={() => navigate('/menu')}
                data-testid="hero-order-btn"
              >
                Order Now <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              {settings?.reservations_enabled && (
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white/30 text-white hover:bg-white/10 font-medium px-8"
                  onClick={() => navigate('/reservations')}
                  data-testid="hero-reserve-btn"
                >
                  Reserve a Table
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Dishes */}
      <section className="py-24 md:py-32" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="font-accent text-secondary text-xl">Must-Try Favorites</span>
            <h2 className="font-serif text-4xl md:text-5xl text-foreground tracking-tight mt-2">
              Our Specialties
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featured.map((item, idx) => (
              <Card 
                key={item.item_id} 
                className="bg-white rounded-xl shadow-sm border border-border/40 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
                data-testid={`featured-item-${idx}`}
              >
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={item.image_url || 'https://images.unsplash.com/photo-1546549032-9571cd6b27df?w=400'} 
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    {item.is_vegetarian && <Badge variant="secondary" className="text-xs"><Leaf className="w-3 h-3 mr-1" /> Veg</Badge>}
                    {item.is_spicy && <Badge variant="destructive" className="text-xs"><Flame className="w-3 h-3 mr-1" /> Spicy</Badge>}
                  </div>
                  <h3 className="font-serif text-xl text-foreground mb-2">{item.name}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{item.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-lg text-primary">${item.price.toFixed(2)}</span>
                    <Button 
                      size="sm" 
                      onClick={() => addItem(item)}
                      data-testid={`add-featured-${idx}`}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => navigate('/menu')}
              className="border-2 border-primary/20 text-primary hover:bg-primary/5 hover:border-primary transition-all font-medium px-8"
              data-testid="view-full-menu-btn"
            >
              View Full Menu <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-24 md:py-32 bg-muted" data-testid="about-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-6">
              <span className="font-accent text-secondary text-xl">Our Story</span>
              <h2 className="font-serif text-4xl md:text-5xl text-foreground tracking-tight mt-2 mb-6">
                A Taste of Home
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                H&S Smoothies & Breakfast is a delightful eatery in Scottdale, Georgia, known for its vibrant menu highlighting both breakfast and smoothie options alongside authentic Eritrean favorites.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Our cozy café offers an inviting atmosphere where customers can enjoy freshly made dishes and beverages crafted with care. We've garnered praise for our warm hospitality and friendly service—making us a popular spot for anyone seeking comfort food.
              </p>
              <Button 
                onClick={() => navigate('/about')}
                className="bg-primary text-white hover:bg-primary/90"
                data-testid="learn-more-btn"
              >
                Learn More About Us
              </Button>
            </div>
            <div className="md:col-span-6">
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_food-commerce-5/artifacts/2etbyvtg_image.png"
                  alt="Ful Special - Our Signature Dish"
                  className="rounded-xl shadow-lg"
                />
                <div className="absolute -bottom-6 -left-6 bg-secondary text-secondary-foreground p-6 rounded-xl shadow-lg">
                  <div className="font-serif text-3xl">Fresh</div>
                  <div className="text-sm">Made Daily</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============== MENU PAGE ==============

const MenuPage = () => {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, itemsRes] = await Promise.all([
          axios.get(`${API}/menu/categories`),
          axios.get(`${API}/menu/items`)
        ]);
        setCategories(catRes.data);
        setItems(itemsRes.data);
        if (catRes.data.length > 0) {
          setActiveCategory(catRes.data[0].category_id);
        }
      } catch (err) {
        toast.error('Failed to load menu');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredItems = activeCategory 
    ? items.filter(item => item.category === activeCategory)
    : items;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="menu-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="py-16 md:py-24" data-testid="menu-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <span className="font-accent text-secondary text-xl">Explore our dishes</span>
          <h1 className="font-serif text-4xl md:text-5xl text-foreground tracking-tight mt-2">
            Our Menu
          </h1>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Category Sidebar */}
          <div className="md:w-64 flex-shrink-0">
            <div className="md:sticky md:top-28">
              <h3 className="font-serif text-lg text-foreground mb-4">Categories</h3>
              <div className="flex md:flex-col gap-2">
                {categories.map(cat => (
                  <Button
                    key={cat.category_id}
                    variant={activeCategory === cat.category_id ? "default" : "ghost"}
                    className="justify-start"
                    onClick={() => setActiveCategory(cat.category_id)}
                    data-testid={`category-${cat.category_id}`}
                  >
                    {cat.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Menu Items Grid */}
          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="menu-grid">
              {filteredItems.map((item, idx) => (
                <Card 
                  key={item.item_id}
                  className="bg-white rounded-xl shadow-sm border border-border/40 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
                  data-testid={`menu-item-${idx}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={item.image_url || 'https://images.unsplash.com/photo-1546549032-9571cd6b27df?w=400'} 
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      {item.is_vegetarian && <Badge variant="secondary" className="text-xs"><Leaf className="w-3 h-3 mr-1" /> Veg</Badge>}
                      {item.is_spicy && <Badge variant="destructive" className="text-xs"><Flame className="w-3 h-3 mr-1" /> Spicy</Badge>}
                    </div>
                    <h3 className="font-serif text-xl text-foreground mb-2">{item.name}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{item.description}</p>
                    {item.ingredients?.length > 0 && (
                      <p className="text-xs text-muted-foreground/70 italic mb-4">
                        {item.ingredients.join(', ')}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-lg text-primary">${item.price.toFixed(2)}</span>
                      <Button 
                        size="sm" 
                        onClick={() => addItem(item)}
                        data-testid={`add-menu-item-${idx}`}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== CART PAGE ==============

const CartPage = () => {
  const { items, updateQuantity, removeItem, total, clearCart } = useCart();
  const { user } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleCheckout = async (asGuest = false) => {
    if (items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (asGuest && (!guestName || !guestEmail)) {
      toast.error('Please enter your name and email');
      return;
    }

    setCheckoutLoading(true);

    try {
      const cartItems = items.map(item => ({
        item_id: item.item_id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        image_url: item.image_url
      }));

      let response;
      if (asGuest) {
        response = await axios.post(`${API}/orders/guest-checkout`, {
          guest_name: guestName,
          guest_email: guestEmail,
          items: cartItems,
          origin_url: window.location.origin
        });
      } else {
        const token = localStorage.getItem('token');
        response = await axios.post(`${API}/orders/authenticated-checkout`, {
          items: cartItems,
          origin_url: window.location.origin
        }, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
      }

      // Redirect to Stripe
      window.location.href = response.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center py-24" data-testid="cart-empty">
        <ShoppingCart className="w-16 h-16 text-muted-foreground mb-4" />
        <h2 className="font-serif text-2xl text-foreground mb-2">Your cart is empty</h2>
        <p className="text-muted-foreground mb-6">Add some delicious Eritrean dishes!</p>
        <Button onClick={() => navigate('/menu')} data-testid="cart-browse-menu">
          Browse Menu
        </Button>
      </div>
    );
  }

  return (
    <div className="py-16 md:py-24" data-testid="cart-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <h1 className="font-serif text-4xl text-foreground mb-8">Your Cart</h1>

        <div className="space-y-4 mb-8">
          {items.map((item, idx) => (
            <Card key={item.item_id} className="p-4" data-testid={`cart-item-${idx}`}>
              <div className="flex items-center gap-4">
                <img 
                  src={item.image_url || 'https://images.unsplash.com/photo-1546549032-9571cd6b27df?w=400'} 
                  alt={item.name}
                  className="w-20 h-20 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <h3 className="font-serif text-lg">{item.name}</h3>
                  <p className="text-muted-foreground">${item.price.toFixed(2)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => updateQuantity(item.item_id, item.quantity - 1)}
                    data-testid={`cart-decrease-${idx}`}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="w-8 text-center font-medium">{item.quantity}</span>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => updateQuantity(item.item_id, item.quantity + 1)}
                    data-testid={`cart-increase-${idx}`}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <span className="font-semibold w-20 text-right">${(item.price * item.quantity).toFixed(2)}</span>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => removeItem(item.item_id)}
                  data-testid={`cart-remove-${idx}`}
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </Card>
          ))}
        </div>

        <Separator className="my-8" />

        <div className="flex justify-between items-center mb-8">
          <span className="font-serif text-2xl">Total</span>
          <span className="font-serif text-3xl text-primary">${total.toFixed(2)}</span>
        </div>

        {/* Checkout Options */}
        <Card className="p-6" data-testid="checkout-options">
          <Tabs defaultValue={user ? "authenticated" : "guest"}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="guest" data-testid="guest-checkout-tab">Guest Checkout</TabsTrigger>
              <TabsTrigger value="authenticated" data-testid="auth-checkout-tab">
                {user ? 'Checkout' : 'Sign In to Checkout'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="guest">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="guestName">Name</Label>
                  <Input 
                    id="guestName"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="Your name"
                    data-testid="guest-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="guestEmail">Email</Label>
                  <Input 
                    id="guestEmail"
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="your@email.com"
                    data-testid="guest-email-input"
                  />
                </div>
                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={() => handleCheckout(true)}
                  disabled={checkoutLoading}
                  data-testid="guest-checkout-btn"
                >
                  {checkoutLoading ? 'Processing...' : 'Proceed to Payment'}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="authenticated">
              {user ? (
                <div className="space-y-4">
                  <p className="text-muted-foreground">
                    Checking out as <span className="font-medium text-foreground">{user.email}</span>
                  </p>
                  <Button 
                    className="w-full" 
                    size="lg"
                    onClick={() => handleCheckout(false)}
                    disabled={checkoutLoading}
                    data-testid="auth-checkout-btn"
                  >
                    {checkoutLoading ? 'Processing...' : 'Proceed to Payment'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-muted-foreground">Sign in to track your orders and earn rewards.</p>
                  <Button 
                    className="w-full" 
                    size="lg"
                    onClick={() => navigate('/login', { state: { from: '/cart' } })}
                    data-testid="signin-to-checkout-btn"
                  >
                    Sign In
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

// ============== ORDER SUCCESS PAGE ==============

const OrderSuccessPage = () => {
  const [status, setStatus] = useState('loading');
  const [order, setOrder] = useState(null);
  const { clearCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      navigate('/');
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;

    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API}/orders/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setOrder(response.data.order);
          clearCart();
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(pollStatus, 2000);
        } else {
          setStatus('timeout');
        }
      } catch (err) {
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(pollStatus, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollStatus();
  }, [clearCart, navigate]);

  if (status === 'loading') {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center" data-testid="order-success-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4" />
        <p className="text-muted-foreground">Confirming your payment...</p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center py-24" data-testid="order-success-confirmed">
        <div className="w-20 h-20 bg-accent rounded-full flex items-center justify-center mb-6">
          <ChefHat className="w-10 h-10 text-accent-foreground" />
        </div>
        <h1 className="font-serif text-4xl text-foreground mb-2">Thank You!</h1>
        <p className="text-muted-foreground mb-6">Your order has been confirmed.</p>
        {order && (
          <Card className="p-6 mb-6 max-w-md w-full">
            <h3 className="font-serif text-lg mb-4">Order #{order.order_id}</h3>
            <div className="space-y-2">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span>{item.name} x{item.quantity}</span>
                  <span>${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold">
                <span>Total</span>
                <span>${order.total?.toFixed(2)}</span>
              </div>
            </div>
          </Card>
        )}
        <Button onClick={() => navigate('/menu')} data-testid="order-success-menu-btn">
          Order More
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center py-24" data-testid="order-success-error">
      <h1 className="font-serif text-3xl text-foreground mb-2">Something went wrong</h1>
      <p className="text-muted-foreground mb-6">
        {status === 'expired' ? 'Your payment session has expired.' : 'We could not confirm your payment.'}
      </p>
      <Button onClick={() => navigate('/cart')} data-testid="order-error-cart-btn">
        Return to Cart
      </Button>
    </div>
  );
};

// ============== AUTH PAGES ==============

const LoginPage = () => {
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate(from);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-16" data-testid="login-page">
      <Card className="w-full max-w-md p-8">
        <CardHeader className="text-center p-0 mb-6">
          <CardTitle className="font-serif text-3xl">Welcome Back</CardTitle>
          <CardDescription>Sign in to your account</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input 
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="relative my-6">
            <Separator />
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
              OR
            </span>
          </div>

          <Button 
            variant="outline" 
            className="w-full" 
            onClick={googleLogin}
            data-testid="login-google"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>
        </CardContent>
        <CardFooter className="p-0 mt-6 justify-center">
          <p className="text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary font-medium hover:underline" data-testid="login-register-link">
              Sign up
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success('Account created!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-16" data-testid="register-page">
      <Card className="w-full max-w-md p-8">
        <CardHeader className="text-center p-0 mb-6">
          <CardTitle className="font-serif text-3xl">Create Account</CardTitle>
          <CardDescription>Join the Habesha Table family</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input 
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
                data-testid="register-name"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="register-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input 
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="register-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="p-0 mt-6 justify-center">
          <p className="text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate('/login');
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
        await checkAuth();
        toast.success('Welcome!');
        navigate('/');
      } catch (err) {
        toast.error('Authentication failed');
        navigate('/login');
      }
    };

    processCallback();
  }, [navigate, checkAuth]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="auth-callback">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
    </div>
  );
};

// ============== ACCOUNT PAGE ==============

const AccountPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchOrders = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/orders/my-orders`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setOrders(response.data);
      } catch (err) {
        console.error('Failed to fetch orders:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [user, authLoading, navigate]);

  if (authLoading || !user) return null;

  return (
    <div className="py-16 md:py-24" data-testid="account-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <h1 className="font-serif text-4xl text-foreground mb-2">My Account</h1>
        <p className="text-muted-foreground mb-8">Welcome, {user.name}!</p>

        <h2 className="font-serif text-2xl text-foreground mb-6">Order History</h2>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : orders.length === 0 ? (
          <Card className="p-8 text-center" data-testid="no-orders">
            <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">You haven't placed any orders yet.</p>
            <Button onClick={() => navigate('/menu')} data-testid="account-browse-menu">
              Browse Menu
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order, idx) => (
              <Card key={order.order_id} className="p-6" data-testid={`order-${idx}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-medium">Order #{order.order_id}</h3>
                    <p className="text-sm text-muted-foreground">
                      {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant={order.status === 'confirmed' ? 'default' : 'secondary'}>
                    {order.status}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {order.items?.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span>{item.name} x{item.quantity}</span>
                      <span>${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <Separator className="my-4" />
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span>${order.total?.toFixed(2)}</span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============== ABOUT PAGE ==============

const AboutPage = () => {
  return (
    <div className="py-16 md:py-24" data-testid="about-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="font-accent text-secondary text-xl">Our Story</span>
          <h1 className="font-serif text-4xl md:text-5xl text-foreground tracking-tight mt-2">
            About H&S Smoothies & Breakfast
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-24">
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_food-commerce-5/artifacts/g8ps34gw_image.png"
              alt="Inside H&S Smoothies with Asmara mural"
              className="rounded-xl shadow-lg"
            />
            <p className="text-sm text-muted-foreground mt-4 italic text-center">
              Our beautiful mural featuring downtown Asmara, Eritrea and the Cathedral of Our Lady of the Rosary
            </p>
          </div>
          <div>
            <h2 className="font-serif text-3xl text-foreground mb-6">A Cozy Taste of Home</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              H&S Smoothies & Breakfast is a delightful eatery located in Scottdale, Georgia, known for its vibrant menu that highlights both breakfast and smoothie options. Our cozy café offers an inviting atmosphere where customers can enjoy freshly made dishes and beverages crafted with care.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We've garnered praise for our warm hospitality and friendly service, making us a popular spot for anyone seeking comfort food. Our smoothies are celebrated for their freshness and rich flavors.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Our interior features a picturesque mural of downtown Asmara, Eritrea—including the historic Cathedral of Our Lady of the Rosary—bringing a piece of Eritrean culture to every visit.
            </p>
            <div className="bg-muted p-6 rounded-lg">
              <h3 className="font-serif text-lg mb-3">Visit Us</h3>
              <p className="text-sm text-muted-foreground mb-1">3500 N Decatur Rd, Scottdale, GA 30079</p>
              <p className="text-sm text-muted-foreground mb-1">(404) 500-4648</p>
              <p className="text-sm text-muted-foreground">Mon-Sat: 8AM-5PM | Sun: 8AM-4PM</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Card className="p-8 text-center">
            <div className="w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <ChefHat className="w-8 h-8 text-secondary" />
            </div>
            <h3 className="font-serif text-xl mb-2">Fresh Daily</h3>
            <p className="text-muted-foreground text-sm">
              All our dishes and smoothies are made fresh daily with quality ingredients.
            </p>
          </Card>
          <Card className="p-8 text-center">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-serif text-xl mb-2">Warm Hospitality</h3>
            <p className="text-muted-foreground text-sm">
              Our friendly service creates a welcoming atmosphere for everyone.
            </p>
          </Card>
          <Card className="p-8 text-center">
            <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Leaf className="w-8 h-8 text-accent" />
            </div>
            <h3 className="font-serif text-xl mb-2">Quality Ingredients</h3>
            <p className="text-muted-foreground text-sm">
              Fresh fruits, authentic spices, and locally sourced produce in every dish.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ============== RESERVATIONS PAGE ==============

const ReservationsPage = () => {
  const { settings } = useSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState('');
  const [partySize, setPartySize] = useState('2');
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  const [requests, setRequests] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  if (!settings?.reservations_enabled) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center py-24" data-testid="reservations-disabled">
        <CalendarIcon className="w-16 h-16 text-muted-foreground mb-4" />
        <h2 className="font-serif text-2xl text-foreground mb-2">Reservations Coming Soon</h2>
        <p className="text-muted-foreground mb-6">We're not accepting reservations at this time.</p>
        <Button onClick={() => navigate('/menu')} data-testid="reservations-browse-menu">
          Order Online Instead
        </Button>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !time || !name || !email || !phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/reservations`, {
        guest_name: name,
        guest_email: email,
        guest_phone: phone,
        party_size: parseInt(partySize),
        date: date.toISOString().split('T')[0],
        time: time,
        special_requests: requests || null
      }, {
        withCredentials: true,
        headers: user ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}
      });

      toast.success('Reservation submitted! We\'ll confirm via email.');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit reservation');
    } finally {
      setLoading(false);
    }
  };

  const timeSlots = [
    '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
    '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM'
  ];

  return (
    <div className="py-16 md:py-24" data-testid="reservations-page">
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <span className="font-accent text-secondary text-xl">Book a table</span>
          <h1 className="font-serif text-4xl text-foreground tracking-tight mt-2">
            Make a Reservation
          </h1>
        </div>

        <Card className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Select Date</Label>
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(date) => date < new Date()}
                  className="rounded-md border mt-2"
                  data-testid="reservation-calendar"
                />
              </div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="time">Select Time</Label>
                  <Select value={time} onValueChange={setTime}>
                    <SelectTrigger data-testid="reservation-time">
                      <SelectValue placeholder="Choose a time" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map(slot => (
                        <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="partySize">Party Size</Label>
                  <Select value={partySize} onValueChange={setPartySize}>
                    <SelectTrigger data-testid="reservation-party-size">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} {n === 1 ? 'Guest' : 'Guests'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input 
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                  data-testid="reservation-name"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input 
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                  data-testid="reservation-phone"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="reservation-email"
              />
            </div>

            <div>
              <Label htmlFor="requests">Special Requests (optional)</Label>
              <Textarea 
                id="requests"
                value={requests}
                onChange={(e) => setRequests(e.target.value)}
                placeholder="Any dietary restrictions or special occasions?"
                data-testid="reservation-requests"
              />
            </div>

            <Button type="submit" className="w-full" size="lg" disabled={loading} data-testid="reservation-submit">
              {loading ? 'Submitting...' : 'Request Reservation'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

// ============== ADMIN DASHBOARD ==============

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const { settings, setSettings } = useSettings();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      try {
        const [ordersRes, itemsRes, reservationsRes] = await Promise.all([
          axios.get(`${API}/admin/orders`, { headers }),
          axios.get(`${API}/admin/menu/items`, { headers }),
          axios.get(`${API}/admin/reservations`, { headers }).catch(() => ({ data: [] }))
        ]);
        setOrders(ordersRes.data);
        setMenuItems(itemsRes.data);
        setReservations(reservationsRes.data);
      } catch (err) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, authLoading, navigate]);

  const handleToggleSetting = async (key, value) => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/admin/settings`, {
        [key]: value
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      toast.success(`${key.replace(/_/g, ' ')} ${value ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/orders/${orderId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, status } : o));
      toast.success('Order updated');
    } catch (err) {
      toast.error('Failed to update order');
    }
  };

  if (authLoading || (!user || user.role !== 'admin')) return null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="py-8 md:py-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <h1 className="font-serif text-4xl text-foreground mb-8">Admin Dashboard</h1>

        <Tabs defaultValue="settings">
          <TabsList className="mb-8">
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders ({orders.length})</TabsTrigger>
            <TabsTrigger value="menu" data-testid="admin-tab-menu">Menu Items</TabsTrigger>
            <TabsTrigger value="reservations" data-testid="admin-tab-reservations">Reservations</TabsTrigger>
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6" data-testid="settings-features">
                <h3 className="font-serif text-xl mb-6">Feature Toggles</h3>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">AI Chatbot</Label>
                      <p className="text-sm text-muted-foreground">Enable AI-powered customer support</p>
                    </div>
                    <Switch
                      checked={settings?.ai_chatbot_enabled || false}
                      onCheckedChange={(v) => handleToggleSetting('ai_chatbot_enabled', v)}
                      disabled={savingSettings}
                      data-testid="toggle-chatbot"
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">AI Recommendations</Label>
                      <p className="text-sm text-muted-foreground">Show AI-powered dish suggestions</p>
                    </div>
                    <Switch
                      checked={settings?.ai_recommendations_enabled || false}
                      onCheckedChange={(v) => handleToggleSetting('ai_recommendations_enabled', v)}
                      disabled={savingSettings}
                      data-testid="toggle-recommendations"
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">Table Reservations</Label>
                      <p className="text-sm text-muted-foreground">Allow customers to book tables</p>
                    </div>
                    <Switch
                      checked={settings?.reservations_enabled || false}
                      onCheckedChange={(v) => handleToggleSetting('reservations_enabled', v)}
                      disabled={savingSettings}
                      data-testid="toggle-reservations"
                    />
                  </div>
                </div>
              </Card>

              <Card className="p-6" data-testid="settings-stats">
                <h3 className="font-serif text-xl mb-6">Quick Stats</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted p-4 rounded-lg">
                    <div className="text-3xl font-bold text-primary">{orders.length}</div>
                    <div className="text-sm text-muted-foreground">Total Orders</div>
                  </div>
                  <div className="bg-muted p-4 rounded-lg">
                    <div className="text-3xl font-bold text-primary">{menuItems.length}</div>
                    <div className="text-sm text-muted-foreground">Menu Items</div>
                  </div>
                  <div className="bg-muted p-4 rounded-lg">
                    <div className="text-3xl font-bold text-primary">{orders.filter(o => o.status === 'confirmed').length}</div>
                    <div className="text-sm text-muted-foreground">Confirmed</div>
                  </div>
                  <div className="bg-muted p-4 rounded-lg">
                    <div className="text-3xl font-bold text-primary">{reservations.length}</div>
                    <div className="text-sm text-muted-foreground">Reservations</div>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card className="p-6" data-testid="admin-orders-list">
              <ScrollArea className="h-[600px]">
                <div className="space-y-4">
                  {orders.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No orders yet</p>
                  ) : (
                    orders.map((order, idx) => (
                      <Card key={order.order_id} className="p-4" data-testid={`admin-order-${idx}`}>
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="font-medium">Order #{order.order_id}</h4>
                            <p className="text-sm text-muted-foreground">
                              {order.guest_name || order.user_id} • {new Date(order.created_at).toLocaleString()}
                            </p>
                          </div>
                          <Select 
                            value={order.status} 
                            onValueChange={(v) => handleUpdateOrderStatus(order.order_id, v)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="confirmed">Confirmed</SelectItem>
                              <SelectItem value="preparing">Preparing</SelectItem>
                              <SelectItem value="ready">Ready</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1 text-sm">
                          {order.items?.map((item, i) => (
                            <div key={i} className="flex justify-between">
                              <span>{item.name} x{item.quantity}</span>
                              <span>${(item.price * item.quantity).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        <Separator className="my-3" />
                        <div className="flex justify-between font-medium">
                          <span>Total</span>
                          <span>${order.total?.toFixed(2)}</span>
                        </div>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>
            </Card>
          </TabsContent>

          {/* Menu Tab */}
          <TabsContent value="menu">
            <Card className="p-6" data-testid="admin-menu-list">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {menuItems.map((item, idx) => (
                  <Card key={item.item_id} className="p-4" data-testid={`admin-menu-item-${idx}`}>
                    <div className="flex gap-4">
                      <img 
                        src={item.image_url || 'https://images.unsplash.com/photo-1546549032-9571cd6b27df?w=100'} 
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div className="flex-1">
                        <h4 className="font-medium">{item.name}</h4>
                        <p className="text-sm text-muted-foreground">${item.price.toFixed(2)}</p>
                        <Badge variant={item.is_available ? 'default' : 'secondary'} className="mt-1 text-xs">
                          {item.is_available ? 'Available' : 'Unavailable'}
                        </Badge>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Reservations Tab */}
          <TabsContent value="reservations">
            <Card className="p-6" data-testid="admin-reservations-list">
              <ScrollArea className="h-[600px]">
                <div className="space-y-4">
                  {reservations.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No reservations yet</p>
                  ) : (
                    reservations.map((res, idx) => (
                      <Card key={res.reservation_id} className="p-4" data-testid={`admin-reservation-${idx}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium">{res.guest_name}</h4>
                            <p className="text-sm text-muted-foreground">{res.guest_email} • {res.guest_phone}</p>
                            <div className="flex gap-4 mt-2 text-sm">
                              <span className="flex items-center gap-1"><CalendarIcon className="w-4 h-4" /> {res.date}</span>
                              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {res.time}</span>
                              <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {res.party_size}</span>
                            </div>
                          </div>
                          <Badge variant={res.status === 'confirmed' ? 'default' : 'secondary'}>
                            {res.status}
                          </Badge>
                        </div>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// ============== AI CHATBOT WIDGET ==============

const ChatbotWidget = () => {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  if (!settings?.ai_chatbot_enabled) return null;

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: input,
        session_id: sessionId
      });
      
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I\'m having trouble right now. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <Button
        onClick={() => setOpen(!open)}
        className="fixed bottom-20 right-6 w-14 h-14 rounded-full shadow-lg z-40"
        data-testid="chatbot-trigger"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </Button>

      {/* Chat Window */}
      {open && (
        <Card className="fixed bottom-36 right-6 w-80 md:w-96 h-[500px] flex flex-col shadow-xl z-40" data-testid="chatbot-window">
          <CardHeader className="bg-primary text-primary-foreground rounded-t-lg py-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Habesha Assistant
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full p-4">
              {messages.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <p className="mb-2">Welcome! How can I help you today?</p>
                  <p className="text-sm">Ask about our menu, ingredients, or recommendations!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, idx) => (
                    <div 
                      key={idx} 
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div 
                        className={`max-w-[80%] p-3 rounded-lg ${
                          msg.role === 'user' 
                            ? 'bg-primary/10 text-foreground' 
                            : 'bg-muted text-foreground'
                        }`}
                      >
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-muted p-3 rounded-lg">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
                          <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                          <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </CardContent>
          <CardFooter className="p-4 border-t">
            <form 
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }} 
              className="flex w-full gap-2"
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me anything..."
                disabled={loading}
                data-testid="chatbot-input"
              />
              <Button type="submit" size="icon" disabled={loading} data-testid="chatbot-send">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </CardFooter>
        </Card>
      )}
    </>
  );
};

// ============== APP ROUTER ==============

const AppRouter = () => {
  const location = useLocation();

  // Check for auth callback before rendering routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/menu" element={<MenuPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/order-success" element={<OrderSuccessPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/reservations" element={<ReservationsPage />} />
      <Route path="/admin" element={<AdminDashboard />} />
    </Routes>
  );
};

// ============== MAIN APP ==============

function App() {
  return (
    <BrowserRouter>
      <SettingsProvider>
        <AuthProvider>
          <CartProvider>
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-1">
                <AppRouter />
              </main>
              <Footer />
              <ChatbotWidget />
            </div>
            <Toaster position="top-right" richColors />
          </CartProvider>
        </AuthProvider>
      </SettingsProvider>
    </BrowserRouter>
  );
}

export default App;
